import useLocalStorage from 'beautiful-react-hooks/useLocalStorage'
import { useMemo } from 'react'
import { useOnce } from '@opallabs/lambda'
import { LocalStorageKeys } from './types'

export type TabName = 'tasks' | 'approvals' | 'notifications' | 'downloads'

export const orderedTabs: TabName[] = [
  'tasks',
  'approvals',
  'notifications',
  'downloads',
]
const hiddenTabsInNonMomentAppsCount = 2

const translateSelectedTabIndexToActiveTab = (
  index: number,
  isMoments: boolean,
  isViewOnlyRole: boolean
): string => {
  let selectedIndex = index
  if (!isMoments || (isMoments && isViewOnlyRole)) {
    selectedIndex += hiddenTabsInNonMomentAppsCount
  }
  if (selectedIndex < 0 || selectedIndex >= orderedTabs.length) {
    return orderedTabs[0]
  }
  return orderedTabs[selectedIndex]
}

const translateActiveTabToSelectedTabIndex = (
  selectedTab: string | null,
  isMomentsApp: boolean,
  isViewOnlyRole: boolean
): number => {
  if (selectedTab === null) {
    return 0
  }

  let selectedIndex = orderedTabs.findIndex((tab) => tab === selectedTab)
  if (!isMomentsApp || (isMomentsApp && isViewOnlyRole)) {
    selectedIndex -= hiddenTabsInNonMomentAppsCount
  }
  if (selectedIndex < 0) {
    return 0
  }
  return selectedIndex
}

interface UseSelectedPrimaryTabArgObject {
  initialTab: TabName | undefined
  isMomentsApp: boolean
  isViewOnlyRole: boolean
  tabChangeCallback?: () => void
}

interface UseSelectedPrimaryTabReturnObject {
  onTabChange: (newTab: number) => void
  selectedIndex: number
}

type UseSelectedPrimaryTab = (
  arg: UseSelectedPrimaryTabArgObject
) => UseSelectedPrimaryTabReturnObject

export const useSelectedPrimaryTab: UseSelectedPrimaryTab = ({
  initialTab,
  isMomentsApp,
  isViewOnlyRole,
  tabChangeCallback,
}) => {
  const [activeTab, setActiveTab] = useLocalStorage<string>(
    LocalStorageKeys.primaryTab,
    'tasks'
  )

  /* if a tab is selected via url fragment, it should take precedence */
  useOnce(() => {
    if (initialTab !== undefined && initialTab !== activeTab) {
      setActiveTab(() => initialTab)
    }
  })

  const onTabChange: (newTab: number) => void = (newTab) => {
    setActiveTab(() =>
      translateSelectedTabIndexToActiveTab(newTab, isMomentsApp, isViewOnlyRole)
    )
    if (tabChangeCallback !== undefined) tabChangeCallback()
  }

  const selectedIndex = useMemo<number>(
    () =>
      translateActiveTabToSelectedTabIndex(
        activeTab,
        isMomentsApp,
        isViewOnlyRole
      ),
    [activeTab, isMomentsApp, isViewOnlyRole]
  )

  return {
    onTabChange,
    selectedIndex,
  }
}
